import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import Navigation from "@/components/Navigation";
import Footer from "@/components/ui/footer";
import { FounderBadge } from "@/components/FounderBadge";
import { OfficialBearInsiderShield } from "@/components/OfficialBearInsiderBadge";
import { OfficialBearInsiderTooltip } from "@/components/OfficialBearInsiderTooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ObjectUploader } from "@/components/ObjectUploader";
import { StoryNicknameDialog } from "@/components/StoryNicknameDialog";
import { VoiceAssignmentDialog } from "@/components/VoiceAssignmentDialog";
import { BookPreview } from "@/components/BookPreview";
import { AddonBalanceDisplay } from "@/components/addons/AddonBalanceDisplay";
import { AddonCatalogCard } from "@/components/addons/AddonCatalogCard";
import { TransactionHistory } from "@/components/addons/TransactionHistory";
import { UsageMeter } from "@/components/addons/UsageMeter";
import { useAddonCatalog, usePostPurchaseHandler } from "@/hooks/addons";
import { useSubscriptionProducts } from "@/hooks/subscription-products";
import { SubscriptionProductCard } from "@/components/subscription/SubscriptionProductCard";
import { User, Shield, BookOpen, Settings as SettingsIcon, Save, Camera, Loader2, Edit3, Trash2, ShoppingCart, Mic, Users, ChevronRight, Gift, Download, Award, Sparkles, Crown, ExternalLink, Code } from "lucide-react";
import { Link } from "wouter";
import type { UploadResult } from "@uppy/core";
import type { User as UserType, Story } from "@shared/schema";

export default function Profile() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { user, isLoading, isFounder, isOfficialBearInsider } = useAuth();

  // Form states
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  // Story nickname dialog state
  const [isNicknameDialogOpen, setIsNicknameDialogOpen] = useState(false);
  const [editingStory, setEditingStory] = useState<Story | null>(null);
  
  // Voice settings dialog state
  const [isVoiceDialogOpen, setIsVoiceDialogOpen] = useState(false);
  const [voiceEditingStory, setVoiceEditingStory] = useState<Story | null>(null);
  
  // Book preview state
  const [expandedBookPreview, setExpandedBookPreview] = useState<number | null>(null);

  // Add-ons data
  const { data: addonCatalogResponse } = useAddonCatalog();
  const { handlePostPurchase } = usePostPurchaseHandler();
  
  // Subscription products for upgrade section
  const { data: subscriptionProductsResponse, isLoading: subscriptionProductsLoading } = useSubscriptionProducts();

  // Fetch user stories
  const { data: userStories = [], refetch: refetchStories } = useQuery({
    queryKey: ["/api/stories"],
    queryFn: async () => {
      try {
        const response = await apiRequest("GET", "/api/stories");
        if (!response.ok) {
          console.warn("Failed to fetch stories:", response.status);
          return [];
        }
        const data = await response.json();
        return data.stories || [];
      } catch (error) {
        console.error("Error fetching stories:", error);
        return []; // Always return an array even on error
      }
    },
    enabled: !!user,
  });

  // Fetch influencer application status
  const { data: influencerApplication } = useQuery({
    queryKey: ["/api/influencer/application"],
    queryFn: async () => {
      try {
        const response = await apiRequest("GET", "/api/influencer/application");
        if (!response.ok) {
          return null;
        }
        const data = await response.json();
        return data.application || null;
      } catch (error) {
        console.error("Error fetching influencer application:", error);
        return null;
      }
    },
    enabled: !!user,
  });

  // Fetch developer account status
  const { data: developerAccountResponse } = useQuery<{ success: boolean; data: { id: number; status: string; tier: string } | null }>({
    queryKey: ["/api/developer/account"],
    enabled: !!user,
  });
  const developerAccount = developerAccountResponse?.data;


  // Populate form when user data loads
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
      });
    }
  }, [user]);

  // Handle post-purchase URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    handlePostPurchase(searchParams);
  }, [handlePostPurchase]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: typeof profileData) => {
      const response = await apiRequest("PATCH", "/api/user/profile", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update profile");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update story nickname mutation
  const updateNicknameMutation = useMutation({
    mutationFn: async ({ storyId, nickname }: { storyId: number; nickname: string }) => {
      const response = await apiRequest("PATCH", `/api/stories/${storyId}`, { nickname });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update story nickname");
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate stories query to refresh all story lists
      queryClient.invalidateQueries({ queryKey: ["/api/stories"] });
      toast({
        title: "Nickname updated",
        description: "Story nickname has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: typeof passwordData) => {
      const response = await apiRequest("POST", "/api/user/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to change password");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Password changed",
        description: "Your password has been changed successfully.",
      });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Password change failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Profile picture upload handler
  const handleUploadComplete = async (
    result: UploadResult<Record<string, unknown>, Record<string, unknown>>
  ) => {
    console.log("Upload complete result:", result);
    
    if (result.successful && result.successful.length > 0) {
      const uploadedFile = result.successful[0];
      console.log("Uploaded file:", uploadedFile);
      setIsUploadingImage(true);
      
      try {
        // Get the upload URL from the response data
        const uploadURL = (uploadedFile.response as any)?.uploadURL;
        
        if (!uploadURL) {
          throw new Error("No upload URL received from server");
        }
        
        console.log("Updating profile picture with URL:", uploadURL);
        
        const response = await apiRequest("PUT", "/api/user/profile-picture", {
          profilePictureURL: uploadURL,
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to update profile picture");
        }
        
        console.log("Profile picture updated successfully");
        
        toast({
          title: "Profile Picture Updated",
          description: "Your profile picture has been successfully updated.",
        });
        
        // Refresh user data
        queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      } catch (error: any) {
        console.error("Profile picture update error:", error);
        toast({
          title: "Upload Failed",
          description: error.message || "Failed to update profile picture",
          variant: "destructive",
        });
      } finally {
        setIsUploadingImage(false);
      }
    } else if (result.failed && result.failed.length > 0) {
      console.error("Upload failed:", result.failed);
      const failedFile = result.failed[0];
      const errorMessage = typeof failedFile.error === 'string' 
        ? failedFile.error 
        : (failedFile.error as any)?.message || "Failed to upload file";
      toast({
        title: "Upload Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileData);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure your new passwords match.",
        variant: "destructive",
      });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }

    changePasswordMutation.mutate(passwordData);
  };

  // Story nickname handlers
  const handleEditNickname = (story: Story) => {
    setEditingStory(story);
    setIsNicknameDialogOpen(true);
  };

  const handleSaveNickname = async (nickname: string) => {
    if (editingStory) {
      updateNicknameMutation.mutate({
        storyId: editingStory.id,
        nickname,
      });
    }
    setIsNicknameDialogOpen(false);
    setEditingStory(null);
  };

  const handleCloseNicknameDialog = () => {
    setIsNicknameDialogOpen(false);
    setEditingStory(null);
  };

  if (!user) {
    navigate("/auth");
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-4">My Profile</h1>
          {isFounder && (
            <div className="flex justify-center items-center gap-4 mb-6">
              <FounderBadge size="large" animated={true} />
              <div className="text-center">
                <p className="text-pink-600 font-semibold text-lg">
                  🌟 Welcome back, Penelope! 🌟
                </p>
                <p className="text-purple-600 text-sm italic">
                  The brilliant mind behind SnuggleCrafters ✨
                </p>
              </div>
            </div>
          )}
        </div>
        
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="stories" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              My Stories
            </TabsTrigger>
            <TabsTrigger value="addons" className="flex items-center gap-2" data-testid="tab-addons">
              <ShoppingCart className="w-4 h-4" />
              Add-ons
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Security
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>
                  Update your personal information and preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Profile Picture Section */}
                <div className="flex flex-col items-center space-y-4 mb-6">
                  <div className="relative">
                    <Avatar className="w-24 h-24 rounded-full border-2 border-purple-500 bg-gray-100">
                      {user?.profilePictureUrl ? (
                        <AvatarImage
                          src={user.profilePictureUrl}
                          alt={`${user.firstName || "User"}'s profile`}
                        />
                      ) : null}
                      <AvatarFallback className="text-2xl">
                        {user?.firstName?.[0] || user?.email?.[0] || "U"}
                      </AvatarFallback>
                    </Avatar>
                    {isOfficialBearInsider && (
                      <div className="absolute -bottom-1 -right-1">
                        <OfficialBearInsiderTooltip showShield={true} size="small">
                          <div></div>
                        </OfficialBearInsiderTooltip>
                      </div>
                    )}
                  </div>

                  <ObjectUploader
                    maxNumberOfFiles={1}
                    maxFileSize={5242880} // 5MB
                    uploadType="profile_picture"
                    onComplete={handleUploadComplete}
                    buttonClassName="max-w-xs"
                  >
                    <div className="flex items-center gap-2">
                      {isUploadingImage ? (
                        <Loader2 className="w-4 h-4 animate-spin text-black" />
                      ) : (
                        <Camera className="w-4 h-4 text-black" />
                      )}
                      <span className="text-black">
                        {isUploadingImage
                          ? "Uploading..."
                          : user?.profilePictureUrl
                          ? "Change Picture"
                          : "Upload Picture"}
                      </span>
                    </div>
                  </ObjectUploader>
                </div>

                {/* Child Profiles Card */}
                <Card className="mb-6 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-teal-50 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/child-profiles")} data-testid="card-child-profiles">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-r from-purple-600 to-teal-600 p-3 rounded-full">
                          <Users className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-purple-700" data-testid="text-child-profiles-title">
                            My Children's Profiles
                          </h3>
                          <p className="text-sm text-purple-600" data-testid="text-child-profiles-description">
                            Manage individual settings for each child's personalized stories
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-6 h-6 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>

                {/* Influencer Program Card */}
                {!influencerApplication && (
                  <Card 
                    className="mb-6 border-2 border-amber-200 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 hover:shadow-lg transition-shadow cursor-pointer" 
                    onClick={() => navigate("/influencer/application")} 
                    data-testid="card-influencer-program"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-3 rounded-full">
                            <Sparkles className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-amber-700" data-testid="text-influencer-program-title">
                                Become an Influencer
                              </h3>
                              <Award className="w-5 h-5 text-amber-600" />
                            </div>
                            <p className="text-sm text-amber-600" data-testid="text-influencer-program-description">
                              Join our influencer program and earn commissions by sharing SnuggleCrafters
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-6 h-6 text-amber-600" />
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {influencerApplication?.status === 'pending' && (
                  <Card 
                    className="mb-6 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 hover:shadow-lg transition-shadow cursor-pointer" 
                    onClick={() => navigate("/influencer/application")} 
                    data-testid="card-influencer-pending"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-3 rounded-full">
                            <Loader2 className="w-6 h-6 text-white animate-spin" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-blue-700" data-testid="text-influencer-pending-title">
                              Application Under Review
                            </h3>
                            <p className="text-sm text-blue-600" data-testid="text-influencer-pending-description">
                              Your influencer application is being reviewed by our team
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-6 h-6 text-blue-600" />
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {influencerApplication?.status === 'approved' && (
                  <Card 
                    className="mb-6 border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 hover:shadow-lg transition-shadow cursor-pointer" 
                    onClick={() => navigate("/influencer/dashboard")} 
                    data-testid="card-influencer-dashboard"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-3 rounded-full">
                            <Award className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-green-700" data-testid="text-influencer-dashboard-title">
                              Influencer Dashboard
                            </h3>
                            <p className="text-sm text-green-600" data-testid="text-influencer-dashboard-description">
                              View your performance, earnings, and promotional resources
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-6 h-6 text-green-600" />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Developer Account Section */}
                {developerAccount ? (
                  <Card 
                    className="mb-6 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 hover:shadow-lg transition-shadow cursor-pointer" 
                    onClick={() => navigate("/developer/dashboard")} 
                    data-testid="card-developer-dashboard"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-3 rounded-full">
                            <Code className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-blue-700" data-testid="text-developer-dashboard-title">
                                Developer Dashboard
                              </h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                developerAccount.status === 'approved' 
                                  ? 'bg-green-100 text-green-700' 
                                  : developerAccount.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {developerAccount.status.charAt(0).toUpperCase() + developerAccount.status.slice(1)}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium capitalize">
                                {developerAccount.tier}
                              </span>
                            </div>
                            <p className="text-sm text-blue-600" data-testid="text-developer-dashboard-description">
                              Manage your API keys and view usage statistics
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-6 h-6 text-blue-600" />
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card 
                    className="mb-6 border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 via-blue-50 to-cyan-50 hover:shadow-lg transition-shadow cursor-pointer" 
                    onClick={() => navigate("/developer-signup")} 
                    data-testid="card-become-developer"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-gradient-to-r from-indigo-500 to-cyan-500 p-3 rounded-full">
                            <Code className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-indigo-700" data-testid="text-become-developer-title">
                                Become a Developer
                              </h3>
                              <ExternalLink className="w-5 h-5 text-indigo-600" />
                            </div>
                            <p className="text-sm text-indigo-600" data-testid="text-become-developer-description">
                              Get API access to integrate AI-powered stories into your applications
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-6 h-6 text-indigo-600" />
                      </div>
                    </CardContent>
                  </Card>
                )}

                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={profileData.firstName}
                        onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                        placeholder="Enter your first name"
                        data-testid="input-first-name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={profileData.lastName}
                        onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                        placeholder="Enter your last name"
                        data-testid="input-last-name"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileData.email}
                      disabled
                      className="bg-gray-50"
                    />
                    <p className="text-sm text-gray-500">Email cannot be changed</p>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={updateProfileMutation.isPending}
                    variant="gradient"
                    className="w-full md:w-auto text-white"
                    data-testid="button-save-profile"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="stories">
            <Card>
              <CardHeader>
                <CardTitle>My Stories</CardTitle>
                <CardDescription>
                  Manage nicknames for your stories to make them easier to find and remember
                </CardDescription>
              </CardHeader>
              <CardContent>
                {userStories.length === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No stories found. Create your first story to get started!</p>
                    <Button 
                      onClick={() => navigate("/home")} 
                      className="mt-4"
                      variant="gradient"
                    >
                      Create Story
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userStories.map((story: Story) => (
                      <div key={story.id} className="space-y-3">
                        <div
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm truncate">
                              {story.nickname || story.title || "Untitled Story"}
                            </h3>
                            {story.nickname && story.title && (
                              <p className="text-xs text-muted-foreground truncate">
                                Original: {story.title}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Created: {new Date(story.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => navigate(`/stories/${story.id}`)}
                              className="h-8"
                              data-testid={`button-view-story-${story.id}`}
                            >
                              <BookOpen className="w-3 h-3 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setVoiceEditingStory(story);
                                setIsVoiceDialogOpen(true);
                              }}
                              className="h-8"
                              data-testid={`button-voice-settings-${story.id}`}
                            >
                              <Mic className="w-3 h-3 mr-1" />
                              Voices
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditNickname(story)}
                              className="h-8"
                              data-testid={`button-edit-nickname-${story.id}`}
                            >
                              <Edit3 className="w-3 h-3 mr-1" />
                              {story.nickname ? "Edit" : "Add"}
                            </Button>
                            <Button
                              variant={expandedBookPreview === story.id ? "secondary" : "gradient"}
                              size="sm"
                              onClick={() => setExpandedBookPreview(
                                expandedBookPreview === story.id ? null : story.id
                              )}
                              className="h-8"
                              data-testid={`button-toggle-pdf-${story.id}`}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          </div>
                        </div>
                        
                        {expandedBookPreview === story.id && (
                          <div className="ml-4 animate-in slide-in-from-top-2 duration-300">
                            <BookPreview
                              storyId={story.id}
                              storyTitle={story.nickname || story.title || "Untitled Story"}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="addons" data-testid="content-addons">
            <div className="space-y-6">
              {/* Current Balances */}
              <AddonBalanceDisplay
                className="w-full"
                showQuickTopUp={true}
                data-testid="addon-balance-display"
              />

              {/* Upgrade Your Plan Section */}
              <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-teal-50" data-testid="card-upgrade-plan">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="h-5 w-5 text-purple-600" />
                    Upgrade Your Plan
                  </CardTitle>
                  <CardDescription>
                    Explore our premium subscriptions and add-ons to unlock more features
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {subscriptionProductsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                    </div>
                  ) : subscriptionProductsResponse?.products && subscriptionProductsResponse.products.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {subscriptionProductsResponse.products
                          .filter((product) => {
                            const category = product.metadata?.category;
                            return product.isActive && (category === 'premium' || category === 'character_studio');
                          })
                          .slice(0, 3)
                          .map((product) => (
                            <SubscriptionProductCard
                              key={product.id}
                              product={product}
                              data-testid={`subscription-product-card-${product.id}`}
                            />
                          ))}
                      </div>
                      <div className="flex justify-center pt-4">
                        <Link href="/store">
                          <Button 
                            variant="gradient" 
                            className="text-white flex items-center gap-2"
                            data-testid="button-view-all-plans"
                          >
                            View All Plans
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Crown className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No subscription products available at this time</p>
                      <Link href="/store">
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          data-testid="button-browse-store"
                        >
                          Browse Store
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Add-on Catalog for Purchase */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Purchase Add-ons
                  </CardTitle>
                  <CardDescription>
                    Buy illustration credits and storage to enhance your storytelling experience
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {addonCatalogResponse?.addons ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {addonCatalogResponse.addons
                        .filter(addon => addon.isActive)
                        .map((addon) => (
                          <AddonCatalogCard
                            key={addon.id}
                            addon={addon}
                            data-testid={`addon-catalog-card-${addon.sku}`}
                          />
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Add-on catalog is currently unavailable</p>
                      <p className="text-sm">Please try again later</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Transaction History */}
              <TransactionHistory
                data-testid="addon-transaction-history"
              />
            </div>
          </TabsContent>
          
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>
                  Update your password to keep your account secure
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      placeholder="Enter your current password"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      placeholder="Enter a new password (min. 8 characters)"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      placeholder="Confirm your new password"
                      required
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={changePasswordMutation.isPending}
                    variant="gradient"
                    className="w-full md:w-auto text-white"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    {changePasswordMutation.isPending ? "Changing..." : "Change Password"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Subscription Info */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">Status:</span>
                  {user?.subscriptionStatus === "lifetime" ? (
                    <span className="text-green-600 font-semibold bg-green-50 px-3 py-1 rounded-full text-sm">
                      ✨ Lifetime Access
                    </span>
                  ) : user?.subscriptionStatus === "active" ? (
                    <span className="text-green-600 font-semibold bg-green-50 px-3 py-1 rounded-full text-sm">
                      Active Subscription
                    </span>
                  ) : user?.subscriptionStatus === "trial" ? (
                    <span className="text-blue-600 font-semibold bg-blue-50 px-3 py-1 rounded-full text-sm">
                      Trial Period
                    </span>
                  ) : (
                    <span className="text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-sm">
                      No Active Subscription
                    </span>
                  )}
                </div>
                
                {user?.subscriptionStatus === "lifetime" && (
                  <div className="text-sm text-gray-600">
                    <p className="font-medium">🎉 You have lifetime access to all features!</p>
                    <p className="mt-1">Thank you for being a valued member of our community.</p>
                  </div>
                )}
                
                {user?.subscriptionEndsAt && user?.subscriptionStatus !== "lifetime" && (
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">Expires:</span>
                    <span className="text-gray-600">
                      {new Date(user.subscriptionEndsAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
                
                {user?.subscriptionStatus === "trial" && (
                  <div className="text-sm text-gray-600">
                    <p>Enjoy unlimited story generation during your trial!</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Button
                  onClick={() => navigate("/subscribe")}
                  variant="gradient"
                  className="w-full text-white"
                >
                  {user?.subscriptionStatus === "lifetime" ? (
                    <>View Subscription Details</>
                  ) : user?.subscriptionStatus === "active" ? (
                    <>Manage Subscription</>
                  ) : (
                    <>Upgrade to Premium</>
                  )}
                </Button>
                
                <Button
                  onClick={() => navigate("/buy-gift-card")}
                  variant="outline"
                  className="w-full flex items-center gap-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50"
                  data-testid="button-buy-gift-card-profile"
                >
                  <Gift className="w-4 h-4" />
                  Buy a Gift Card
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
      
      {/* Story Nickname Dialog */}
      <StoryNicknameDialog
        isOpen={isNicknameDialogOpen}
        onClose={handleCloseNicknameDialog}
        onSave={handleSaveNickname}
        initialNickname={editingStory?.nickname || ""}
      />
      
      {/* Voice Assignment Dialog */}
      {voiceEditingStory && (
        <VoiceAssignmentDialog
          open={isVoiceDialogOpen}
          onOpenChange={(open) => {
            setIsVoiceDialogOpen(open);
            if (!open) setVoiceEditingStory(null);
          }}
          storyId={voiceEditingStory.id}
          storyContent={voiceEditingStory.content || ''}
          currentCharacterMap={voiceEditingStory.characterMap || undefined}
        />
      )}
    </div>
  );
}